import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';
import SignupPrompt from '../Account/SignupPrompt';

interface FollowButtonProps {
  targetUserId: string;
}

const FollowButton: React.FC<FollowButtonProps> = ({ targetUserId }) => {
  const { currentUser } = useAuth();
  console.log('FollowButton rendered', { target: targetUserId, me: currentUser?.uid });
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  const followId = currentUser ? `${currentUser.uid}_${targetUserId}` : '';

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'follows', followId))
      .then(snap => setFollowing(snap.exists()))
      .catch(console.error);
  }, [currentUser, followId]);

  // Guests and your own profile get no button
  if (currentUser?.uid === targetUserId) return null;

  const toggle = async () => {
    if (!currentUser) { setShowSignupPrompt(true); return; }
    if (busy) return;
    setBusy(true);
    const next = !following;
    setFollowing(next); // optimistic
    try {
      if (next) {
        await setDoc(doc(db, 'follows', followId), {
          followerId: currentUser.uid,
          followedId: targetUserId,
          createdAt: serverTimestamp()
        });
      } else {
        await deleteDoc(doc(db, 'follows', followId));
      }
    } catch (err) {
      console.error('Follow toggle failed:', err);
      setFollowing(!next); // revert
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={toggle}
        disabled={busy}
        aria-pressed={following}
        style={{
          minHeight: '44px',
          padding: '10px 24px',
          borderRadius: '9999px',
          border: following ? '2px solid #6a37d4' : 'none',
          background: following ? 'white' : 'linear-gradient(135deg, #6a37d4, #ae8dff)',
          color: following ? '#6a37d4' : 'white',
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: '14px',
          fontWeight: 800,
          cursor: busy ? 'not-allowed' : 'pointer'
        }}
      >
        {following ? '✓ Following' : '+ Follow'}
      </button>
      <SignupPrompt
        isOpen={showSignupPrompt}
        onClose={() => setShowSignupPrompt(false)}
        reason="follow"
      />
    </>
  );
};

export default FollowButton;